/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-warning-comments */

// eslint-disable-next-line simple-import-sort/imports
import { startPayment } from 'api/simple_payment'
import { AnimatedError } from 'components/animatedError'
import { ConfirmButton, ConfirmButtonInverted } from 'components/button/button.styled'
import { CustomForm } from 'components/form/customForm'
import { FormField } from 'components/form/customFormField'
import { ModeSelection } from 'components/modeSelection'
import { usePayment } from 'hooks/payment/usePayment'
import { useCustomerForm } from 'hooks/user/useCustomer'
import { useTicketsLockedByUser } from 'hooks/user/useTickets/useTicketsLockedByUser'
import { Checkbox } from 'primereact/checkbox'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { EventDetails, Ticket } from 'types/Event'
import { FRONTEND_BASE_URL } from 'utils'

import { DataHandlingWrapper, FormContent, Label, SectionTitle, Text } from './index.style'
import { useToken } from 'hooks/token'
import { useReservationTimer } from 'hooks/user/useTickets/useReservationTimer'
import { Row } from 'primereact/row'

interface CustomerFormProps {
    currentEvent: EventDetails
}

export const CustomerForm = ({ currentEvent }: CustomerFormProps) => {
    const { control, handleSubmit, errors, mode, setMode, watch, setValue, validateForm } = useCustomerForm({
        onClose: () => console.log('Form closed'),
        // TODO: create route, redirect here, fill data, merge cart data with customerdata, call otp
    })
    const { sessionId } = useToken()

    const [selectedTicketIds, setSelectedTickets] = useState<string[]>([])
    const [acceptedDataHandling, setAcceptedDataHandling] = useState(false)
    const [buttonAlreadyClicked, setButtonAlreadyClicked] = useState(false)
    const formRef = useRef<HTMLFormElement>(null)

    const {
        data: ticketsLockedByUser,
        isLoading: ticketsLockedByUserIsLoading,
        error: ticketsLockedByUserError,
        refetch: ticketsLockedByUserRefetch,
    } = useTicketsLockedByUser(currentEvent.event_id, sessionId ?? '')
    const { formatted: reservationTime } = useReservationTimer(ticketsLockedByUser)

    const seatIds = useMemo(() => {
        if (!ticketsLockedByUser) return []
        return ticketsLockedByUser.map((t: Ticket) => t.seat_id)
    }, [ticketsLockedByUser])

    const { handlePayment, selectedSeats, isLoadingPayment, paymentError } = usePayment({
        currentEvent,
        getCustomerData: () => ({
            firstName: watch('firstName'),
            lastName: watch('lastName'),
            phoneNumber: watch('phoneNumber'),
            email: watch('email'),
        }),
        getInvoiceData: () => ({
            country: watch('country'),
            postCode: watch('postCode'),
            city: watch('city'),
            address: watch('address'),
            taxNumber: watch('taxNumber'),
            companyName: watch('companyName'),
            type: mode,
        }),
        startProcess: startPayment,
        urlPrefix: `${FRONTEND_BASE_URL}/jegyek/letoltes`,
        seatIdsOverride: seatIds,
    })

    const getTickets = useCallback(() => {
        setSelectedTickets((prev) => [
            ...prev,
            ...selectedSeats
                .map((seatId) => currentEvent.tickets?.find((ticket) => ticket.seat_id === seatId)?.ticket_id)
                .filter((ticketId): ticketId is string => ticketId !== undefined),
        ])
    }, [selectedSeats, currentEvent.tickets])

    useEffect(() => {
        if (selectedSeats.length > 0 && currentEvent.tickets) {
            getTickets()
        }
    }, [selectedSeats, currentEvent.tickets, getTickets])

    return (
        <CustomForm
            handleSubmit={handleSubmit}
            onClose={() => console.log('Form closed')}
            formRef={formRef}
            loading={false}
            disableButtons
            hasLine={false}
        >
            <ModeSelection
                options={[
                    { value: 'individual', label: 'Magánszemély' },
                    { value: 'organization', label: 'Adószámmal rendelkező szervezet' },
                ]}
                selectedValue={mode}
                buttonClassName="my-button"
                selectedButtonClassName="selected"
                onChange={(value: string) => setMode()}
            />
            <FormContent>
                {reservationTime && (
                    <Row>
                        <strong style={{ color: '#D32F2F' }}>{reservationTime}</strong>
                    </Row>
                )}
                <SectionTitle>Kapcsolattartási adatok</SectionTitle>
                <FormField
                    name="email"
                    control={control}
                    errors={errors}
                    label="Email cím"
                    placeholder="Adja meg az email címét"
                    rules={{
                        required: 'Az email cím megadása kötelező',
                        pattern: { value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, message: 'Érvénytelen email cím formátum' },
                    }}
                />
                <FormField
                    name="confirmEmail"
                    control={control}
                    errors={errors}
                    label="Email cím megerősítése"
                    placeholder="Erősítse meg az email címét"
                    rules={{
                        required: 'Kérjük, erősítse meg az email címét',
                        validate: (value: string) => value === watch('email') || 'Az email címek nem egyeznek',
                    }}
                />

                <FormField
                    name="lastName"
                    control={control}
                    errors={errors}
                    label="Vezetéknév"
                    placeholder="Adja meg a vezetéknevét"
                    rules={{ required: 'A vezetéknév megadása kötelező' }}
                />
                <FormField
                    name="firstName"
                    control={control}
                    errors={errors}
                    label="Keresztnév"
                    placeholder="Adja meg a keresztnevét"
                    rules={{ required: 'A keresztnév megadása kötelező' }}
                />

                <FormField
                    name="phoneNumber"
                    control={control}
                    errors={errors}
                    label="Telefonszám"
                    placeholder="+36123456789"
                    rules={{ required: 'A telefonszám megadása kötelező' }}
                />

                <SectionTitle>Számlázási adatok</SectionTitle>
                {mode === 'organization' && (
                    <>
                        <FormField
                            name="companyName"
                            control={control}
                            errors={errors}
                            label="Vállalkozás neve"
                            placeholder="Adja meg a vállalkozás nevét"
                            rules={{ required: 'A vállalkozás nevének megadása kötelező' }}
                        />

                        <FormField
                            name="taxNumber"
                            control={control}
                            errors={errors}
                            label="Adószám"
                            placeholder="Adja meg az adószámot"
                            rules={{ required: 'Az adószám megadása kötelező szervezetek számára' }}
                        />
                    </>
                )}

                <FormField
                    name="country"
                    control={control}
                    errors={errors}
                    label="Ország"
                    placeholder="Adja meg az országot"
                    rules={{ required: 'Az ország megadása kötelező' }}
                />
                <FormField
                    name="postCode"
                    control={control}
                    errors={errors}
                    label="Irányítószám"
                    placeholder="Adja meg az irányítószámot"
                    rules={{ required: 'Az irányítószám megadása kötelező' }}
                />
                <FormField
                    name="city"
                    control={control}
                    errors={errors}
                    label="Város"
                    placeholder="Adja meg a várost"
                    rules={{ required: 'A város megadása kötelező' }}
                />
                <FormField
                    name="address"
                    control={control}
                    errors={errors}
                    label="Cím"
                    placeholder="Adja meg a címet"
                    rules={{ required: 'A cím megadása kötelező' }}
                />
                <DataHandlingWrapper>
                    <Label>
                        <Checkbox
                            inputId="acceptDataHandling"
                            checked={acceptedDataHandling}
                            onChange={(e) => setAcceptedDataHandling(e.checked ?? false)}
                        />
                        <Text>
                            Tudomásul veszem, hogy a(z) SPEC-VÉD Bt. (2462 Martonvásár Fehérvári út 77.) adatkezelő által a martonticket.hu
                            felhasználói adatbázisában tárolt alábbi személyes adataim átadásra kerülnek a SimplePay Zrt., mint
                            adatfeldolgozó részére. Az adatkezelő által továbbított adatok köre az alábbi: kifizetendő összeg (HUF), ügyfél
                            email címe. Az adatfeldolgozó által végzett adatfeldolgozási tevékenység jellege és célja a SimplePay
                            Adatkezelési tájékoztatóban, az alábbi linken tekinthető meg:{' '}
                            <a href="https://simplepay.hu/adatkezelesi-tajekoztatok/" target="_blank" rel="noopener noreferrer">
                                https://simplepay.hu/adatkezelesi-tajekoztatok/
                            </a>
                        </Text>
                    </Label>
                </DataHandlingWrapper>
                <ConfirmButton
                    onClick={async () => {
                        if (buttonAlreadyClicked) return
                        setButtonAlreadyClicked(true)

                        try {
                            await validateForm()
                            if (!acceptedDataHandling) {
                                setButtonAlreadyClicked(false)
                                return
                            }
                            await handlePayment()
                        } catch (error) {
                            console.warn('Validációs hiba a számlázási információkban', error)
                        } finally {
                            setTimeout(() => setButtonAlreadyClicked(false), 10000)
                        }
                    }}
                    disabled={isLoadingPayment || !acceptedDataHandling || buttonAlreadyClicked}
                >
                    {isLoadingPayment ? 'Fizetés...' : 'Fizetés'}
                </ConfirmButton>
                {process.env.NODE_ENV === 'development' && (
                    <ConfirmButtonInverted
                        onClick={() => {
                            setValue('email', 'forrasmisii@gmail.com')
                            setValue('confirmEmail', 'forrasmisii@gmail.com')
                            setValue('lastName', 'Teszt')
                            setValue('firstName', 'Béci')
                            setValue('phoneNumber', '+36123456789')
                            setValue('country', 'Magyarország')
                            setValue('postCode', '1234')
                            setValue('city', 'Budapest')
                            setValue('address', 'Teszt utca 1.')
                            setValue('companyName', 'Teszt Kft.')
                            setValue('taxNumber', '12345678-1-12')
                        }}
                    >
                        Tesztadatok kitöltése
                    </ConfirmButtonInverted>
                )}
                {paymentError && <AnimatedError>{paymentError}</AnimatedError>}
            </FormContent>
        </CustomForm>
    )
}
